/**
 * FERP PDF Export Component (DRR-079)
 *
 * Uses pdf-lib to flatten annotation overlays into a downloadable PDF.
 * READ-ONLY export only — source of truth is DynamoDB annotations.
 *
 * REGISTERED EXCEPTION: Custom Visualization per METADATA_DRIVEN_GOVERNANCE.md
 * - pdf-lib only performs read-only transforms on download
 * - Frontend does NOT modify the source of truth
 * - Annotations live in DynamoDB, pdf-lib only runs on export
 * - See: .claude/FRONTEND_CUSTOMIZATION_REGISTRY.md (EXC-PDF-001)
 *
 * FERP Constraints:
 * - Mantine v8.x ONLY
 * - This is the ONLY pdf-lib usage in frontend (registered exception)
 */

import { useState, useCallback } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import type { PdfAnnotation } from './types';

const STAMP_RGB: Record<string, { r: number; g: number; b: number }> = {
  APPROVED: { r: 0.13, g: 0.55, b: 0.13 },
  REJECTED: { r: 0.85, g: 0.18, b: 0.18 },
  REVIEWED: { r: 0.13, g: 0.39, b: 0.72 },
};

const HIGHLIGHT_RGB: Record<string, { r: number; g: number; b: number }> = {
  yellow: { r: 1.0, g: 0.92, b: 0.23 },
  green: { r: 0.3, g: 0.69, b: 0.31 },
  blue: { r: 0.13, g: 0.59, b: 0.95 },
  red: { r: 0.96, g: 0.26, b: 0.21 },
};

interface ExportResult {
  /** Whether export is in progress */
  exporting: boolean;
  /** Error message if export failed */
  error: string | null;
  /** Trigger the export */
  exportPdf: () => Promise<void>;
}

/**
 * Hook to export a PDF with flattened annotations.
 *
 * @param pdfUrl - URL to the source PDF
 * @param annotations - All annotations to flatten
 * @param filename - Download filename
 */
export function usePdfExport(
  pdfUrl: string,
  annotations: PdfAnnotation[],
  filename: string,
): ExportResult {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportPdf = useCallback(async () => {
    setExporting(true);
    setError(null);

    try {
      // Fetch the source PDF
      const existingPdfBytes = await fetch(pdfUrl).then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
        return res.arrayBuffer();
      });

      // Load into pdf-lib
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const pages = pdfDoc.getPages();

      // Flatten each annotation onto the PDF
      for (const ann of annotations) {
        const pageIndex = ann.page - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex]!;
        const { width, height } = page.getSize();
        const { rect } = ann;

        // Convert relative coordinates to absolute
        const absX = rect.x * width;
        const absY = height - (rect.y + rect.height) * height; // PDF Y is bottom-up
        const absW = rect.width * width;
        const absH = rect.height * height;

        if (ann.type === 'highlight') {
          const hlColor = HIGHLIGHT_RGB[ann.color ?? 'yellow'] ?? HIGHLIGHT_RGB['yellow']!;
          page.drawRectangle({
            x: absX,
            y: absY,
            width: absW,
            height: absH,
            color: rgb(hlColor.r, hlColor.g, hlColor.b),
            opacity: 0.35,
          });
        } else if (ann.type === 'text') {
          // Draw comment note background
          page.drawRectangle({
            x: absX,
            y: absY,
            width: Math.max(absW, 100),
            height: Math.max(absH, 30),
            color: rgb(1.0, 0.95, 0.88),
            borderColor: rgb(1.0, 0.65, 0.0),
            borderWidth: 0.5,
            opacity: 0.95,
          });
          // Draw comment text
          const fontSize = 8;
          page.drawText(ann.content, {
            x: absX + 3,
            y: absY + Math.max(absH, 30) - fontSize - 3,
            size: fontSize,
            font: helveticaFont,
            color: rgb(0, 0, 0),
            maxWidth: Math.max(absW, 100) - 6,
          });
          // Draw author
          page.drawText(ann.createdBy, {
            x: absX + 3,
            y: absY + 3,
            size: 6,
            font: helveticaFont,
            color: rgb(0.5, 0.5, 0.5),
          });
        } else if (ann.type === 'stamp') {
          const stColor = STAMP_RGB[ann.content] ?? STAMP_RGB['REVIEWED']!;
          const fontSize = 14;
          const textWidth = helveticaBold.widthOfTextAtSize(ann.content, fontSize);
          const padding = 6;
          const stampW = textWidth + padding * 2;
          const stampH = fontSize + padding * 2;
          const stampX = absX;
          const stampY = absY;

          // Draw stamp border
          page.drawRectangle({
            x: stampX,
            y: stampY,
            width: stampW,
            height: stampH,
            borderColor: rgb(stColor.r, stColor.g, stColor.b),
            borderWidth: 2,
            color: rgb(1, 1, 1),
            opacity: 0.9,
          });
          // Draw stamp text
          page.drawText(ann.content, {
            x: stampX + padding,
            y: stampY + padding,
            size: fontSize,
            font: helveticaBold,
            color: rgb(stColor.r, stColor.g, stColor.b),
          });
        }
      }

      // Serialize and download
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.endsWith('.pdf') ? filename : `${filename}-annotated.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [pdfUrl, annotations, filename]);

  return { exporting, error, exportPdf };
}
